import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import axios from "axios";
import { setGlobalGroupBy } from "store/features/groupBy/groupBySlice";
import { RootState } from "store/rootReducer";
import { toPdenEnum } from "utils/toPdenEnum";

import { aggregationTypeToInt } from "api/data";

import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";
import {
  AggregationType,
  BinType,
  DataType,
  IGroupBy,
  PerformanceDateRange,
  QuantileState,
  isAggregationCompatible
} from "models/groupBy";

const dataRoot = process.env.REACT_APP_DATA_RS_ROOT;

// Holds the new mode field while a numeric bin fetch is in flight.
// The popover reads this for immediate title/type feedback instead of waiting
// for the single deferred Redux dispatch that commits field + bin together.
interface PendingModeField {
  property: string;
  title: string;
  dataType: DataType;
  canBin?: boolean;
  aggregation: AggregationType;
  performanceDateRange?: PerformanceDateRange;
}

const emptyBin: mBinSize = {
  BinSize: "",
  LessThan: null,
  GreaterThan: null,
  MinSize: "",
  BinType: "BinSize",
  Quantile: null,
  IsLocked: false,
  UseDynamicBins: false
};

export const useGroupModeController = (entityKind: EntityKind) => {
  const dispatch = useDispatch();

  const groupBy = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );

  const filterId = useSelector((state: RootState) => {
    const reducer = entityKind === EntityKind.Well ? state.filter : state.facilityFilter;
    return reducer?.["filterId"] as { id: string } | undefined;
  });

  const isGroupMode = !!groupBy?.mode;

  const [localBin, setLocalBin] = useState<mBinSize>(
    () => groupBy?.mode?.bin ?? emptyBin
  );
  // isFetchingBinDefaults: true while the bin-size API request is in flight.
  // Drives the disabled state on <BinSettings> so the user cannot edit stale
  // values during the fetch.
  const [isFetchingBinDefaults, setIsFetchingBinDefaults] = useState(false);
  const pendingFetchPropertyRef = useRef<string | null>(null);
  // binFetchError: true when the most recent fetch threw an error.
  // Drives the inline error notice rendered above <BinSettings>.
  const [binFetchError, setBinFetchError] = useState(false);
  // binFetchUnsupported: true when the bin-size fetch returned HTTP 400 due to the
  // Phase 1 limitation (outer group property uses bin settings / can_bin = true).
  // Renders a specific actionable message rather than the generic fetch error notice.
  const [binFetchUnsupported, setBinFetchUnsupported] = useState(false);

  const [pendingModeField, setPendingModeField] = useState<PendingModeField | null>(null);
  const pendingModeFieldRef = useRef<PendingModeField | null>(null);

  // mBinSize has a fixed key set; insertion order is stable so stringify is a safe equality key
  const modeBinJson = JSON.stringify(groupBy?.mode?.bin);
  useEffect(() => {
    if (groupBy?.mode?.bin) {
      setLocalBin(groupBy.mode.bin);
    }
  }, [modeBinJson]); // eslint-disable-line react-hooks/exhaustive-deps

  // §7.1 CONTRACT DEVIATION (see STATE_OWNERSHIP_CONTRACT.md Decision Log 2026-04-17):
  // This function calls axios.get directly instead of using React Query for the
  // /api/v1/data/bin-size endpoint. The deviation is intentional: the fetch result
  // must be committed to Redux atomically with a pending field selection in a single
  // dispatch (the deferred-dispatch pattern). A declarative useQuery call would require
  // redesigning that pattern at disproportionate scope for this phase.
  // Migration target: refactor to useQuery when groupBySlice → Zustand FocusStore
  // migration occurs (see docs/state-management/group-mode-state-metadata.md).
  const fetchAndApplyBinDefaults = useCallback(
    async (
      property: string,
      pdenSource: string,
      useDynamicBins: boolean,
      // isLocked: callers pass false for new-field / reset fetches, and the existing
      // lock state for filterId-retry fetches (so the user's lock isn't cleared on reload).
      isLocked = false,
      performanceDateRange?: PerformanceDateRange,
      // groupModeContext: triggers the backend's group-mode-aware bin-size path (REQ-BS-001),
      // which computes defaults over aggregated group values rather than raw per-well values.
      groupModeContext?: { outerProperty: string; aggregation: AggregationType }
    ) => {
      if (!filterId?.id) return;

      setIsFetchingBinDefaults(true);
      setBinFetchError(false);
      setBinFetchUnsupported(false);
      pendingFetchPropertyRef.current = property;

      const encodedProperty = btoa(encodeURIComponent(property));
      const params = new URLSearchParams({
        normalizeField: "''",
        per: "0",
        pdenSource: toPdenEnum(pdenSource).toString(),
        entityKind: entityKind,
        useDynamicBins: useDynamicBins.toString()
      });
      if (groupModeContext) {
        params.set("groupModeProperty", groupModeContext.outerProperty);
        params.set(
          "groupModeAggregation",
          aggregationTypeToInt(groupModeContext.aggregation).toString()
        );
      }
      if (performanceDateRange) {
        params.set("startDate", performanceDateRange.startDate);
        params.set("endDate", performanceDateRange.endDate);
      }
      const url = `${dataRoot}/api/v1/data/bin-size/${filterId.id}/${encodedProperty}?${params}`;

      try {
        const { data } = await axios.get(url);
        if (data?.isValid) {
          const fetchedBin: mBinSize = {
            BinSize: data.binSize ?? "",
            LessThan: data.lessThan === "" ? null : data.lessThan,
            GreaterThan: data.greaterThan === "" ? null : data.greaterThan,
            MinSize: data.minSize ?? "",
            BinType: "BinSize",
            Quantile: null,
            UseDynamicBins: useDynamicBins,
            IsLocked: isLocked
          };
          // Read current state at dispatch time to avoid clobbering concurrent field changes
          // that occurred while this async fetch was in flight.
          dispatch((_, getState) => {
            const state = getState() as RootState;
            const current =
              entityKind === EntityKind.Well
                ? state.groupBy.globalGroupBy
                : state.groupBy.globalFacilityFocus;

            const pending = pendingModeFieldRef.current;

            if (pending && pending.property === property) {
              // Numeric field change: commit pending field selection + fetched bin in one
              // dispatch so only a single POST /map is triggered.
              if (!current?.mode) {
                // Group mode was turned off during the fetch — discard.
                setPendingModeField(null);
                pendingModeFieldRef.current = null;
                return;
              }
              setLocalBin(fetchedBin);
              setPendingModeField(null);
              pendingModeFieldRef.current = null;
              dispatch(
                setGlobalGroupBy({
                  ...current,
                  mode: {
                    ...current.mode,
                    property: pending.property,
                    title: pending.title,
                    dataType: pending.dataType,
                    canBin: pending.canBin,
                    aggregation: pending.aggregation,
                    performanceDateRange: pending.performanceDateRange,
                    bin: fetchedBin
                  }
                })
              );
            } else {
              // Toggle-on path or filterId-retry: just update the bin on the current mode.
              if (!current?.mode) return; // group mode turned off during fetch
              if (current.mode.property !== property) return; // stale fetch — discard
              setLocalBin(fetchedBin);
              dispatch(
                setGlobalGroupBy({
                  ...current,
                  mode: { ...current.mode, bin: fetchedBin }
                })
              );
            }
          });
        } else {
          // API returned isValid: false — surface an error so the user knows
          // bin defaults could not be computed (e.g. the field has no data).
          if (pendingFetchPropertyRef.current === property) {
            setBinFetchError(true);
            setPendingModeField(null);
            pendingModeFieldRef.current = null;
          }
        }
      } catch (e) {
        // network or parse error
        // Guard matches the finally block: only surface an error for the most
        // recent fetch. A stale-request failure must not clobber a healthy state
        // that a newer fetch has already committed.
        if (pendingFetchPropertyRef.current === property) {
          // 400 = Phase 1 limitation: outer property is a binned-numeric field.
          // Surface a specific message so the user understands why bin settings
          // are unavailable, rather than a generic "could not load defaults" notice.
          if (axios.isAxiosError(e) && e.response?.status === 400) {
            setBinFetchUnsupported(true);
          } else {
            setBinFetchError(true);
          }
          // Clear the pending field — it was never committed to Redux.
          // Without this, handleAggregationChange would keep routing into the
          // pending-ref guard and silently discard every aggregation change the
          // user makes after the error notice appears.
          setPendingModeField(null);
          pendingModeFieldRef.current = null;
        }
      } finally {
        // Only clear loading if this is still the most recent fetch request
        if (pendingFetchPropertyRef.current === property) {
          setIsFetchingBinDefaults(false);
        }
      }
    },
    [filterId, entityKind, dispatch]
  );

  // Retry the fetch when filterId becomes available after mount. This handles
  // the case where filterId.id is empty (selector not yet resolved) at the
  // moment the user toggles Group Mode on or changes the field. The correct
  // defaults will load as soon as the ID is populated — no user action required.
  //
  // filterId?.id is the only trigger; the other deps are intentionally omitted
  // to avoid re-fetching on every groupBy change.
  useEffect(() => {
    if (!filterId?.id || !groupBy?.mode) return;
    const isNumeric =
      groupBy.mode.dataType === "Number" || groupBy.mode.dataType === "Integer";
    if (isNumeric) {
      fetchAndApplyBinDefaults(
        groupBy.mode.property,
        groupBy.pdenSource,
        groupBy.mode.bin?.UseDynamicBins ?? false,
        groupBy.mode.bin?.IsLocked ?? false, // preserve lock on filterId retry
        groupBy.mode.performanceDateRange
      );
    }
  }, [filterId?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleGroupMode = useCallback(
    (checked: boolean) => {
      // Clear any pending numeric field change — toggle supersedes it.
      setPendingModeField(null);
      pendingModeFieldRef.current = null;
      if (checked) {
        const newGroupBy = {
          ...groupBy,
          mode: {
            property: groupBy.property,
            title: groupBy.title,
            bin: { ...groupBy.bin },
            dataType: groupBy.dataType,
            canBin: groupBy.canBin, // REQ-UI-GRP-003: carry canBin into mode for suppression check
            aggregation: AggregationType.Default,
            performanceDateRange: groupBy.performanceDateRange
          }
        };
        dispatch(setGlobalGroupBy(newGroupBy));

        const isNumeric = groupBy.dataType === "Number" || groupBy.dataType === "Integer";
        if (isNumeric) {
          fetchAndApplyBinDefaults(
            groupBy.property,
            groupBy.pdenSource,
            groupBy.bin?.UseDynamicBins ?? false,
            false, // fresh defaults — not locked
            groupBy.performanceDateRange
          );
        }
      } else {
        dispatch(setGlobalGroupBy({ ...groupBy, mode: undefined }));
      }
    },
    [dispatch, groupBy, fetchAndApplyBinDefaults]
  );

  const handleFieldChange = useCallback(
    (
      selectedField: IGroupBy,
      _isForecastToggleOn: boolean,
      _forecastFolder: string,
      _forecastFolderName: string,
      _pdenSource,
      performanceDateRange?: PerformanceDateRange
    ) => {
      if (!groupBy?.mode) return;

      const currentAggregation = groupBy.mode.aggregation ?? AggregationType.Default;
      // Aggregation applies to the focus field (inner property). Only reset it
      // if the current selection is incompatible with the focus type, which
      // doesn't change when the user picks a different group field.
      const needsReset = !isAggregationCompatible(currentAggregation, groupBy.dataType);
      const aggregation = needsReset ? AggregationType.Default : currentAggregation;

      const isNumeric =
        selectedField.dataType === "Number" || selectedField.dataType === "Integer";

      if (isNumeric) {
        // Option A: defer Redux dispatch until bin fetch resolves.
        // Store the new field in local state so the popover can update its UI
        // immediately (title, isNumericField check, BinSettings disabled state)
        // without triggering a second POST /map from an intermediate Redux write.
        const pending: PendingModeField = {
          property: selectedField.property,
          title: selectedField.title,
          dataType: selectedField.dataType,
          canBin: selectedField.canBin,
          aggregation,
          performanceDateRange
        };
        setPendingModeField(pending);
        pendingModeFieldRef.current = pending;
        fetchAndApplyBinDefaults(
          selectedField.property,
          selectedField.pdenSource ?? groupBy.pdenSource,
          selectedField.bin?.UseDynamicBins ?? false,
          false, // fresh defaults for new field — not locked
          performanceDateRange
        );
      } else {
        // Non-numeric: no bin fetch needed — dispatch immediately and clear any
        // stale pending numeric selection.
        setPendingModeField(null);
        pendingModeFieldRef.current = null;
        dispatch(
          setGlobalGroupBy({
            ...groupBy,
            mode: {
              ...groupBy.mode,
              property: selectedField.property,
              title: selectedField.title,
              dataType: selectedField.dataType,
              canBin: selectedField.canBin, // REQ-UI-GRP-003: update canBin when field changes
              performanceDateRange,
              ...(selectedField.bin != null ? { bin: selectedField.bin } : {}),
              ...(needsReset ? { aggregation: AggregationType.Default } : {})
            }
          })
        );
      }
    },
    [dispatch, groupBy, fetchAndApplyBinDefaults]
  );

  const handleAggregationChange = useCallback(
    (aggregation: AggregationType) => {
      if (!groupBy?.mode) return;

      // If a numeric field fetch is in flight, keep pendingModeField in sync so
      // the Select stays visually responsive and the deferred commit uses the
      // user's chosen aggregation rather than overwriting it.
      if (pendingModeFieldRef.current) {
        const updated = { ...pendingModeFieldRef.current, aggregation };
        setPendingModeField(updated);
        pendingModeFieldRef.current = updated;
        return;
      }

      dispatch(setGlobalGroupBy({ ...groupBy, mode: { ...groupBy.mode, aggregation } }));
    },
    [dispatch, groupBy]
  );

  // Individual field changes (BinSize, LessThan, GreaterThan, MinSize, MaxBins)
  // Only updates local state — global dispatch happens in handleBinSettingChange
  // when the user clicks "Update Bin Settings".
  const handleBinChange = useCallback(
    (changes: Partial<mBinSize>) => {
      if (!groupBy?.mode) return;
      setLocalBin((prev) => ({ ...prev, ...changes }));
    },
    [groupBy]
  );

  // Bin type / quantile / dynamic flag changes
  const handleBinSettingChange = useCallback(
    (binType: BinType, quantile: QuantileState, useDynamicBins: boolean) => {
      if (!groupBy?.mode) return;
      // "Dynamic" is a UI alias for BinSize + UseDynamicBins=true
      const resolvedBinType = binType === "Dynamic" ? "BinSize" : binType;
      const updated: mBinSize = {
        ...localBin,
        BinType: resolvedBinType,
        Quantile: quantile,
        UseDynamicBins: useDynamicBins
      };
      setLocalBin(updated);
      dispatch(
        setGlobalGroupBy({
          ...groupBy,
          mode: { ...groupBy.mode, bin: updated }
        })
      );
    },
    [localBin, groupBy, dispatch]
  );

  // Reset — re-fetches from API for numeric fields
  const handleBinReset = useCallback(() => {
    if (!groupBy?.mode) return;
    const isNumeric =
      groupBy.mode.dataType === "Number" || groupBy.mode.dataType === "Integer";
    if (isNumeric) {
      // KNOWN LIMITATION: GroupModeOptionsDto has no pdenSource field, so we fall
      // back to groupBy.pdenSource (the outer group-by field's pdenSource). When the
      // mode field's pdenSource differs from the outer field's, Reset may return
      // subtly different defaults than the initial field-change fetch.
      // To resolve precisely: cache the pdenSource used during the initial fetch in a
      // ref — e.g. `const initialModePdenSourceRef = useRef<string>("")` — and read
      // from that ref here instead of groupBy.pdenSource.
      fetchAndApplyBinDefaults(
        groupBy.mode.property,
        groupBy.pdenSource,
        groupBy.mode.bin?.UseDynamicBins ?? false,
        false, // reset explicitly unlocks
        groupBy.mode.performanceDateRange
      );
    }
  }, [groupBy, fetchAndApplyBinDefaults]);

  // Dynamic bin lock/unlock
  const handleBinLock = useCallback(
    (locked?: boolean) => {
      if (!groupBy?.mode) return;
      const updated = {
        ...localBin,
        IsLocked: locked ?? !localBin.IsLocked
      };
      setLocalBin(updated);
      dispatch(
        setGlobalGroupBy({
          ...groupBy,
          mode: { ...groupBy.mode, bin: updated }
        })
      );
    },
    [localBin, groupBy, dispatch]
  );

  // During a numeric field-change fetch, pendingModeField carries the new dataType
  // so the popover correctly shows BinSettings (with inputs disabled) before the
  // deferred Redux commit lands.
  const modeDataType: DataType | undefined =
    pendingModeField?.dataType ?? groupBy?.mode?.dataType;

  return {
    groupBy,
    isGroupMode,
    modeDataType,
    localBin,
    isFetchingBinDefaults,
    binFetchError,
    binFetchUnsupported,
    pendingModeField,
    handleToggleGroupMode,
    handleFieldChange,
    handleAggregationChange,
    handleBinChange,
    handleBinSettingChange,
    handleBinReset,
    handleBinLock
  };
};

/**
 * Lightweight read-only alternative to useGroupModeController.
 *
 * Use this in components that only need to READ isGroupMode / groupBy
 * (e.g. DataTable, DataTableActions, useDataQuery, ExportButton).
 * It does NOT register any local state, effects, or bin-fetch side effects,
 * so it is safe to call in multiple places without triggering duplicate
 * bin-size API requests.
 */
export const useGroupModeState = (entityKind: EntityKind) => {
  const groupBy = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );
  return { groupBy, isGroupMode: !!groupBy?.mode };
};
